import { useEffect, useState } from "react";
import { Button, Dropdown } from "react-bootstrap";
import "./Dashboard.css";
import { Link, useNavigate } from "react-router-dom";
import StudentTable from "./StudentTable";
import TeacherTable from "./TeacherTable";
import { useGetSchoolAdminDashboard } from "../../../../hooks/school/dashboard/useGetDashboard";
import { useUpdatePassingPercentage } from "../../../../hooks/school/dashboard/useUpdatePassingPercentage";
import { useUpdateLocale } from "../../../../hooks/school/dashboard/useUpdateLocale";
import { useUpdateQuizAttempt } from "../../../../hooks/school/quizz/useUpdateQuizAttempt";
import { useGetQuizAttempt } from "../../../../hooks/school/quizz/useGetQuizAttempt";
import SchoolAdminDashboardSkeleton from "./SchoolAdminDashboardSkeleton";
import { LANGUAGE_OPTIONS } from "../../../../constant/constant";
import DashboardErrorState from "./DashboardErrorState";
import SuccessModal from "../../../common/SuccessModal";
import DeleteModal from "../../../common/DeleteModal";
import RoleGuard from "../../../auth/RoleGuard";
import { SchoolAdminStudent } from "../Student/Students";
import { useGetDummyStudent } from "../../../../hooks/school/student/useGetDummyStudent";
import { useSwitchDummyStudent } from "../../../../hooks/school/student/useSwitchDummyStudent";
import { useTenantAuth } from "../../../../context/TeanantAuthProvider";
import { useAuthStudent } from "../../../context/StudentLoginContext/StudentAuthContext";
// import { useGoLiveContext } from "../../../context/SchoolGoLiveContext/GoLiveContext";
// import toast from "react-hot-toast";

export const SchoolAdminDashboard = () => {
  // const { data } = useGoLiveContext();
  // const canAddStudent = data?.can_go_live || data?.has_go_lived;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    /** @type {"teachers" | "students"} */("teachers"),
  );
  const { dashboard, getDashboard, loading, error } =
    useGetSchoolAdminDashboard();
  const { updatePassingPercentage, loading: updatingPercentage } =
    useUpdatePassingPercentage();

  const { updateQuizAttempt, loading: updatingAttempts } =
    useUpdateQuizAttempt();

  const { updateLanguagePreference, loading: updatingLanguage } =
    useUpdateLocale();

  const [selectedAttempts, setSelectedAttempts] = useState(0);
  const [selectedPercentage, setSelectedPercentage] = useState("0%");
  const [quizAttemptType, setQuizAttemptType] = useState("allowed");
  const [originalQuizAttemptType, setOriginalQuizAttemptType] =
    useState("allowed");

  // Language preference state
  const [selectedLanguage, setSelectedLanguage] = useState(
    /** @type {"en" | "hi" | null} */(null),
  );
  const [originalLanguage, setOriginalLanguage] = useState(
    /** @type {"en" | "hi" | null} */(null),
  );

  // Modal state for success/error feedback
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    type: "success",
    title: "",
    message: "",
  });

  // Confirmation modal state for "Teacher Decides" switch
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Language preference confirmation modal state
  const [showLanguageConfirmModal, setShowLanguageConfirmModal] =
    useState(false);
  const [langSaveSuccess, setLangSaveSuccess] = useState(false);

  // Explore Student Flow state
  const [showExploreModal, setShowExploreModal] = useState(false);
  const [exploreLoading, setExploreLoading] = useState(false);
  const [exploreError, setExploreError] = useState("");
  const [exploreSuccess, setExploreSuccess] = useState(false);
  const { getDummyStudent } = useGetDummyStudent();
  const { switchDummyStudent } = useSwitchDummyStudent();
  const { login: tenantLogin } = useTenantAuth();
  const { studentLogin } = useAuthStudent();
  const [langSaveError, setLangSaveError] = useState("");

  const { quizAttempt, getQuizAttempt } = useGetQuizAttempt();

  // Extract all values safely
  const totalStudents = dashboard?.total_students ?? 0;
  const totalTeachers = dashboard?.total_teachers ?? 0;
  const totalQuests = dashboard?.total_quests ?? 0;

  const percentageOptions = [
    "30%",
    "40%",
    "45%",
    "50%",
    "55%",
    "60%",
    "65%",
    "70%",
    "80%",
    "85%",
    "90%",
    "95%",
  ];

  useEffect(() => {
    getDashboard();
    getQuizAttempt();
  }, []);

  useEffect(() => {
    if (dashboard) {
      // Set percentage from API
      const percent = dashboard?.passing_percentage?.current;
      if (percent) setSelectedPercentage(percent + "%");

      // Quiz attempts
      const attempts = dashboard?.allowed_attempts?.current;

      if (dashboard?.allowed_attempts?.mode === "teacher") {
        setQuizAttemptType("teacher");
        setOriginalQuizAttemptType("teacher");
      } else {
        setQuizAttemptType("allowed");
        setOriginalQuizAttemptType("allowed");
        if (attempts) setSelectedAttempts(attempts);
      }

      // Language preference
      const locale = dashboard?.locale ?? null;
      setSelectedLanguage(locale);
      setOriginalLanguage(locale);
    }
  }, [dashboard]);

  useEffect(() => {
    if (!quizAttempt) return;

    if (quizAttempt.teacher_decides === true) {
      // Teacher decides
      setQuizAttemptType("teacher");
      setOriginalQuizAttemptType("teacher");
    } else {
      // Allowed attempts
      setQuizAttemptType("allowed");
      setOriginalQuizAttemptType("allowed");

      if (quizAttempt.school_limit) {
        setSelectedAttempts(quizAttempt.school_limit);
      }
    }
  }, [quizAttempt]);

  /**
   * Handle refresh when error occurs
   */
  const handleRefresh = () => {
    getDashboard();
    getQuizAttempt();
  };

  const handleUpdateAttempts = async () => {
    // If switching from "Allowed Attempts" to "Teacher Decides", show confirmation first
    if (
      originalQuizAttemptType === "allowed" &&
      quizAttemptType === "teacher"
    ) {
      setShowConfirmModal(true);
      return;
    }
    await performUpdateAttempts();
  };

  const performUpdateAttempts = async () => {
    await updateQuizAttempt({
      updatedData: {
        teacher_decides: quizAttemptType === "teacher",
        quiz_attempts: quizAttemptType === "allowed" ? selectedAttempts : null,
      },
      onSuccess: () => {
        getQuizAttempt();
        setOriginalQuizAttemptType(quizAttemptType);
        setModalConfig({
          type: "success",
          title: "Successfully Updated!",
          message:
            quizAttemptType === "teacher"
              ? "Quiz attempts settings updated. Teachers will now decide the number of attempts."
              : `Quiz attempts updated successfully to ${selectedAttempts} attempts.`,
        });
        setShowModal(true);
      },
      onError: (errorMessage) => {
        setModalConfig({
          type: "error",
          title: "Update Failed",
          message: errorMessage,
        });
        setShowModal(true);
      },
    });
  };

  const handleConfirmTeacherDecides = () => {
    setShowConfirmModal(false);
    performUpdateAttempts();
  };

  const handleCancelTeacherDecides = () => {
    setShowConfirmModal(false);
    setQuizAttemptType("allowed");
  };

  /**
   * Handle passing percentage update
   * @param {string} option - The selected percentage option (e.g., "40%")
   */
  const handleUpdatePassingPercentage = async (option) => {
    setSelectedPercentage(option);
    const percentNumber = parseInt(option);

    await updatePassingPercentage({
      percentage: percentNumber,
      onSuccess: () => {
        setModalConfig({
          type: "success",
          title: "Successfully Updated!",
          message: `Passing percentage updated successfully to ${option}.`,
        });
        setShowModal(true);
      },
      onError: (errorMessage) => {
        setModalConfig({
          type: "error",
          title: "Update Failed",
          message: errorMessage,
        });
        setShowModal(true);
      },
    });
  };

  /**
   * Handle language preference save — show confirmation modal first
   */
  const handleSaveLanguagePreference = () => {
    setLangSaveSuccess(false);
    setLangSaveError("");
    setShowLanguageConfirmModal(true);
  };

  /**
   * Actually perform the language preference update after confirmation
   */
  const performLanguageSave = async () => {
    await updateLanguagePreference({
      locale: selectedLanguage,
      onSuccess: () => {
        setOriginalLanguage(selectedLanguage);
        setLangSaveSuccess(true);
      },
      onError: (errorMessage) => {
        setLangSaveError(errorMessage);
      },
    });
  };

  const handleCloseLanguageModal = () => {
    setShowLanguageConfirmModal(false);
    if (!langSaveSuccess) {
      setSelectedLanguage(originalLanguage);
    }
  };

  // Show error state with refresh option (early return for critical error)
  if (error) {
    return <DashboardErrorState error={error} onRefresh={handleRefresh} />;
  }

  return (
    <>
      <div className="container-fluid p-0">
        <div className="p-0" id="">
          {/* Show skeleton for stats section while loading, otherwise show actual content */}
          {loading ? (
            <SchoolAdminDashboardSkeleton />
          ) : (
            <section>
              <div className="container-fluid">
                {/* Header Section */}
                <div className="row mb-3 align-items-center">
                  <div className="col-md-6 col-12">
                    <h2 className="dashboard-title">School Dashboard</h2>
                    <p className="dashboard-subtitle">
                      Default landing page for the school dashboard
                    </p>
                  </div>
                  <div className="col-md-6 col-12 d-flex flex-wrap justify-content-start justify-content-md-end mt-2 mt-md-0 gap-2">
                    <RoleGuard permission="dashboard_add_teacher">
                      <Link
                        to="/school-admin/teachers/create"
                        className="dashboard-btn"
                      >
                        Add Teacher
                      </Link>
                    </RoleGuard>
                    <RoleGuard permission="students_create">
                      <Link
                        to="/school-admin/students/create"
                        className="dashboard-btn"
                      >
                        Add Student
                      </Link>
                    </RoleGuard>
                    <button
                      className="dashboard-btn"
                      onClick={() => setShowExploreModal(true)}
                      id="explore-student-flow-btn"
                    >
                      Explore Student Flow
                    </button>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="row g-3 mb-4">
                  <div className="col-lg-6 col-md-6">
                    <div className="stat-card stat-card-purple">
                      <div className="stat-card-icon">
                        <i className="bi bi-mortarboard"></i>
                      </div>
                      <div className="stat-card-content h-100">
                        <p className="stat-card-label">Total Students</p>
                        <h3 className="stat-card-value">{totalStudents}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-6">
                    <div className="stat-card stat-card-green h-100">
                      <div className="stat-card-icon">
                        <i className="bi bi-person"></i>
                      </div>
                      <div className="stat-card-content">
                        <p className="stat-card-label">Total Teachers</p>
                        <h3 className="stat-card-value">{totalTeachers}</h3>
                      </div>
                    </div>
                  </div>

                  <div className="col-lg-6 col-md-6">
                    <div className="stat-card stat-card-orange h-100">
                      <div className="stat-card-icon">
                        <i className="bi bi-flag-fill"></i>
                      </div>
                      <div className="stat-card-content">
                        <p className="stat-card-label">Total Quests</p>
                        <h3 className="stat-card-value">{totalQuests}</h3>
                      </div>
                    </div>
                  </div>
                  <RoleGuard permission="dashboard_passing_percentage">
                    <div className="col-lg-6 col-md-6">
                      <div className="stat-card stat-card-blue h-100">
                        <div className="stat-card-icon">
                          <i className="bi bi-flag-fill"></i>
                        </div>
                        <div className="stat-card-content">
                          <p className="stat-card-label mb-0">
                            Set Minimum Passing Percentage
                          </p>
                          <Dropdown className="stat-card-dropdown">
                            <Dropdown.Toggle
                              className="percentage-dropdown-btn"
                              id="percentage-dropdown"
                              disabled={updatingPercentage || updatingAttempts}
                            >
                              {selectedPercentage}
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="dropdown-menu-scrollable">
                              {percentageOptions.map((option) => (
                                <Dropdown.Item
                                  key={option}
                                  active={selectedPercentage === option}
                                  onClick={() =>
                                    handleUpdatePassingPercentage(option)
                                  }
                                >
                                  {option}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                          {updatingPercentage && (
                            <p className="text-muted d-block mt-1">
                              Updating Minimum passing percentage...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </RoleGuard>
                </div>

                {/* Quiz Attempts Section */}
                <RoleGuard permission="dashboard_quiz_attempts">
                  <fieldset className="quiz-attempts-fieldset">
                    <legend className="quiz-attempts-legend">
                      Quiz Attempts
                    </legend>
                    <div className="quiz-attempts-content">
                      <div className="quiz-attempts-controls">
                        <div className="form-check quiz-radio">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="quizAttemptType"
                            id="allowedAttempts"
                            checked={quizAttemptType === "allowed"}
                            onChange={() => setQuizAttemptType("allowed")}
                            disabled={
                              updatingAttempts ||
                              updatingPercentage ||
                              originalQuizAttemptType === "teacher"
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="allowedAttempts"
                          >
                            Allowed Attempts
                          </label>
                        </div>

                        <Dropdown className="stat-card-dropdown stat-card-dropdown-bordered">
                          <Dropdown.Toggle
                            className="percentage-dropdown-btn"
                            disabled={
                              quizAttemptType !== "allowed" ||
                              updatingAttempts ||
                              updatingPercentage ||
                              originalQuizAttemptType === "teacher"
                            }
                            id="attempts-dropdown"
                          >
                            {selectedAttempts}
                          </Dropdown.Toggle>

                          <Dropdown.Menu className="dropdown-menu-scrollable">
                            {Array.from({ length: 20 }, (_, i) => i + 1).map(
                              (option) => (
                                <Dropdown.Item
                                  key={option}
                                  onClick={() => setSelectedAttempts(option)}
                                  active={selectedAttempts === option}
                                >
                                  {option}
                                </Dropdown.Item>
                              ),
                            )}
                          </Dropdown.Menu>
                        </Dropdown>

                        <div className="form-check quiz-radio">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="quizAttemptType"
                            id="teacherDecides"
                            checked={quizAttemptType === "teacher"}
                            onChange={() => setQuizAttemptType("teacher")}
                            disabled={
                              updatingAttempts ||
                              updatingPercentage ||
                              originalQuizAttemptType === "teacher"
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="teacherDecides"
                          >
                            Teacher Decides
                          </label>
                        </div>
                      </div>

                      <Button
                        className="dashboard-btn save-btn"
                        disabled={
                          updatingAttempts ||
                          updatingPercentage ||
                          originalQuizAttemptType === "teacher"
                        }
                        onClick={handleUpdateAttempts}
                      >
                        {updatingAttempts ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </fieldset>
                </RoleGuard>

                {/* Language Preference Section */}
                <fieldset className="quiz-attempts-fieldset">
                  <legend className="quiz-attempts-legend">
                    Language Preference
                  </legend>
                  <div className="quiz-attempts-content">
                    <div className="quiz-attempts-controls">
                      {LANGUAGE_OPTIONS.map((option) => (
                        <div
                          className="form-check quiz-radio"
                          key={option.label}
                        >
                          <input
                            className="form-check-input"
                            type="radio"
                            name="languagePreference"
                            id={`lang-${option.label}`}
                            checked={selectedLanguage === option.value}
                            onChange={() => setSelectedLanguage(option.value)}
                            disabled={updatingLanguage}
                          />
                          <label
                            className="form-check-label"
                            htmlFor={`lang-${option.label}`}
                          >
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>

                    <Button
                      className="dashboard-btn save-btn"
                      disabled={
                        updatingLanguage ||
                        selectedLanguage === originalLanguage
                      }
                      onClick={handleSaveLanguagePreference}
                    >
                      {updatingLanguage ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </fieldset>
              </div>
            </section>
          )}
          <div>
            <div className="tabs-container pb-3">
              <button
                className={`tab-btn ${activeTab === "teachers" ? "active" : ""
                  }`}
                onClick={() => setActiveTab("teachers")}
              >
                Teachers
              </button>
              <button
                className={`tab-btn ${activeTab === "students" ? "active" : ""
                  }`}
                onClick={() => setActiveTab("students")}
              >
                Students
              </button>
            </div>
            <div>
              {activeTab === "teachers" ? (
                <TeacherTable RoleGuard={RoleGuard} />
              ) : (
                <SchoolAdminStudent type="dashboard" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal for switching to Teacher Decides */}
      <SuccessModal
        showModal={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        type="error"
        title="Are you sure?"
        message="Switching to 'Teacher Decides' is irreversible. To revert back to 'Allowed Attempts', you will need to manually remove the quiz limits from all classrooms and students."
        primaryButtonText="Yes, Confirm"
        secondaryButtonText="Cancel"
        onPrimaryClick={handleConfirmTeacherDecides}
        onSecondaryClick={handleCancelTeacherDecides}
      />

      {/* Confirmation Modal for Language Preference change */}
      <DeleteModal
        showModal={showLanguageConfirmModal}
        handleClose={handleCloseLanguageModal}
        handleDelete={performLanguageSave}
        loading={updatingLanguage}
        message="Changing the language preference will affect all new sessions. Users who are currently logged in will continue using their previous language. The new language will take effect after they log in again."
        error={langSaveError}
        success={langSaveSuccess}
        successMessage={`Language preference updated to ${LANGUAGE_OPTIONS.find((opt) => opt.value === selectedLanguage)?.label || "Student Decides"}.`}
        customConfirmTitle="Confirm Language Change"
        customSuccessTitle="Updated Successfully"
        customErrorTitle="Update Failed"
        customActionButtonText="Yes, Confirm"
        customLoadingButtonText="Saving..."
      />

      {/* Status Modal for success/error feedback */}
      <SuccessModal
        showModal={showModal}
        handleClose={() => setShowModal(false)}
        type={modalConfig.type}
        title={modalConfig.title}
        message={modalConfig.message}
        primaryButtonText="Close"
      />

      {/* Explore Student Flow Confirmation Modal */}
      <DeleteModal
        showModal={showExploreModal}
        handleClose={() => {
          if (!exploreLoading) {
            setShowExploreModal(false);
            setExploreError("");
            setExploreSuccess(false);
          }
        }}
        handleDelete={async () => {
          try {
            setExploreLoading(true);
            setExploreError("");
            setExploreSuccess(false);

            // Step 1: Create / ensure the dummy student account exists
            await getDummyStudent();

            // Step 2: Switch to the dummy student session
            await switchDummyStudent((data) => {
              const user = data.user;
              user.auth = data.auth;
              user.is_dummy_student = true;
              tenantLogin(user, data.token);

              setExploreSuccess(true);

            });
          } catch {
            setExploreError(
              "Failed to switch to student account. Please try again.",
            );
          } finally {
            setExploreLoading(false);
          }
        }}
        loading={exploreLoading}
        error={exploreError}
        success={exploreSuccess}
        message="Are you sure you want to explore the student flow? You will be automatically logged out from this account and logged in as a student."
        successMessage="Switched to student account successfully. Redirecting..."
        customConfirmTitle="Explore Student Flow"
        customSuccessTitle="Switching Account"
        customErrorTitle="Switch Failed"
        customActionButtonText="Yes, Explore"
        customLoadingButtonText="Switching..."
        onSuccessContinue={() => {
          navigate("/student/start-your-quest");
        }}
      />
    </>
  );
};
